import React, { useEffect, useMemo, useState } from "react";
import api from "../api/client";
import { useToast } from "../components/ToastProvider";

const emptyForm = { name: "", email: "", contact: "" };

export default function Employees() {
  const toast = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm);

  const load = async () => {
    setLoading(true);
    try {
      const response = await api.get("/api/admin/employees?includeInactive=true");
      setRows(response?.data?.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load employees");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeCount = useMemo(() => rows.filter((r) => Number(r.isActive) === 1 || r.isActive === true).length, [rows]);

  const onCreateChange = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
  const onEditChange = (e) => setEditForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const createEmployee = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post("/api/admin/employees", form);
      toast.success("Employee created.");
      setForm(emptyForm);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to create employee");
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (emp) => {
    setEditingId(emp.empid);
    setEditForm({ name: emp.name || "", email: emp.email || "", contact: emp.contact || "" });
  };

  const saveEdit = async () => {
    if (!editingId) return;
    try {
      await api.put(`/api/admin/employees/${editingId}`, editForm);
      toast.success("Employee updated.");
      setEditingId(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update employee");
    }
  };

  const deactivate = async (empid) => {
    if (!window.confirm("Deactivate this employee?")) return;
    try {
      await api.delete(`/api/admin/employees/${empid}`);
      toast.success("Employee deactivated.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to deactivate employee");
    }
  };

  return (
    <div className="rs-page">
      <div className="rs-page__header">
        <div>
          <h1 className="rs-h1">Employees</h1>
          <div className="rs-muted">{activeCount} active employees</div>
        </div>
        <button className="rs-btn rs-btn--outline" onClick={load} disabled={loading}>
          {loading ? "Loading..." : "Refresh"}
        </button>
      </div>

      <div className="rs-card">
        <div className="rs-card__header">
          <div className="rs-card__title">Add employee</div>
          <div className="rs-card__subtitle">Create a new employee record</div>
        </div>
        <form className="rs-form-inline" onSubmit={createEmployee}>
          <input className="rs-input" name="name" value={form.name} onChange={onCreateChange} placeholder="Name" required />
          <input className="rs-input" name="email" value={form.email} onChange={onCreateChange} placeholder="Email" required />
          <input className="rs-input" name="contact" value={form.contact} onChange={onCreateChange} placeholder="Contact" />
          <button className="rs-btn rs-btn--primary" disabled={creating} type="submit">
            {creating ? "Saving..." : "Add"}
          </button>
        </form>
      </div>

      <div className="rs-card">
        <div className="rs-card__header">
          <div className="rs-card__title">All employees</div>
        </div>
        <div className="rs-table-wrap">
          <table className="rs-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Status</th>
                <th>Name</th>
                <th>Email</th>
                <th>Contact</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="rs-empty">
                    {loading ? "Loading..." : "No employees"}
                  </td>
                </tr>
              ) : (
                rows.map((r) => {
                  const isActive = Number(r.isActive) === 1 || r.isActive === true;
                  const isEditing = Number(editingId) === Number(r.empid);
                  return (
                    <tr key={r.empid}>
                      <td data-label="ID">
                        <div className="rs-td__value">{r.empid}</div>
                      </td>
                      <td data-label="Status">
                        <div className="rs-td__value">
                          {isActive ? (
                            <span className="rs-pill rs-pill--success">Active</span>
                          ) : (
                            <span className="rs-pill rs-pill--muted">Inactive</span>
                          )}
                        </div>
                      </td>
                      <td data-label="Name">
                        <div className="rs-td__value">
                          {isEditing ? (
                            <input className="rs-input" name="name" value={editForm.name} onChange={onEditChange} />
                          ) : (
                            r.name
                          )}
                        </div>
                      </td>
                      <td data-label="Email">
                        <div className="rs-td__value">
                          {isEditing ? (
                            <input className="rs-input" name="email" value={editForm.email} onChange={onEditChange} />
                          ) : (
                            r.email
                          )}
                        </div>
                      </td>
                      <td data-label="Contact">
                        <div className="rs-td__value">
                          {isEditing ? (
                            <input
                              className="rs-input"
                              name="contact"
                              value={editForm.contact}
                              onChange={onEditChange}
                            />
                          ) : (
                            r.contact || "-"
                          )}
                        </div>
                      </td>
                      <td data-label="Actions">
                        <div className="rs-td__value rs-actions">
                        {isEditing ? (
                          <>
                            <button className="rs-btn rs-btn--primary" onClick={saveEdit} type="button">
                              Save
                            </button>
                            <button className="rs-btn rs-btn--ghost" onClick={() => setEditingId(null)} type="button">
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <button className="rs-btn rs-btn--outline" onClick={() => startEdit(r)} type="button">
                              Edit
                            </button>
                            {isActive ? (
                              <button className="rs-btn rs-btn--danger" onClick={() => deactivate(r.empid)} type="button">
                                Deactivate
                              </button>
                            ) : null}
                          </>
                        )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
